import unauthenticatedFetch from 'services/rest/unauthenticatedFetch';
import {
  isBaseNetworkError,
  isClientError,
  isServerError,
} from 'shared/utils/errors';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

export enum ValidationResult {
  VALID = 'VALID',
  EXPIRED = 'EXPIRED',
  INVALID = 'INVALID',
}

export enum ReferralSource {
  REFERRAL_LINK = 'referral_link',
  BLANK = 'blank',
}

export interface IValidationResponseBody {
  validationResult?: ValidationResult;
  senderName?: string;
}

export interface ReferralCodeInput {
  referralCode: string;
}

export enum ReferralValidationFailure {
  UNAVAILABLE = 'UNAVAILABLE',
  MALFORMED = 'MALFORMED',
}

export type ReferralValidationOutcome =
  ValidationResult | ReferralValidationFailure;

export interface ReferralValidation {
  outcome: ReferralValidationOutcome;
  senderName?: string;
  failureReason?: string;
}

const KNOWN_VALIDATION_RESULTS = new Set<string>(
  Object.values(ValidationResult),
);

const toReferralValidation = (
  body: IValidationResponseBody | null,
): ReferralValidation => {
  const validationResult = body?.validationResult;
  if (validationResult && KNOWN_VALIDATION_RESULTS.has(validationResult)) {
    return { outcome: validationResult, senderName: body?.senderName };
  }
  return {
    outcome: ReferralValidationFailure.MALFORMED,
    failureReason: `validationResult=${String(validationResult)}`,
  };
};

export const describeReferralValidationFailure = (error: unknown): string => {
  if (isClientError(error) || isServerError(error)) {
    return `${error.name} ${error.statusCode}`;
  }
  if (error instanceof Error) {
    return error.name;
  }
  return 'UnknownError';
};

const parseValidationBody = async (
  response: Response,
): Promise<ReferralValidation> => {
  try {
    return toReferralValidation(
      (await response.json()) as IValidationResponseBody | null,
    );
  } catch (error) {
    if (!(error instanceof SyntaxError)) {
      throw error;
    }
    return {
      outcome: ReferralValidationFailure.MALFORMED,
      failureReason: 'InvalidJSON',
    };
  }
};

const postValidateReferralCodeRequest = async (
  lambdaUrl: string,
  data: ReferralCodeInput,
  signal?: AbortSignal,
): Promise<ReferralValidation> => {
  try {
    const response = await unauthenticatedFetch(`${lambdaUrl}`, {
      method: 'POST',
      cache: 'no-cache',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        ...data,
      }),
    });
    return await parseValidationBody(response);
  } catch (error) {
    if (isBaseNetworkError(error)) {
      const failureReason = describeReferralValidationFailure(error);
      logBreadcrumb({
        message: `/postValidateReferralCodeRequest failed: ${error.message}`,
        level: 'error',
        data: { failureReason },
      });
      return {
        outcome: ReferralValidationFailure.UNAVAILABLE,
        failureReason,
      };
    }
    throw error;
  }
};

export default postValidateReferralCodeRequest;
